import {
  GPU_USAGE_PERIOD_OPTIONS,
  type GpuUsagePeriod,
} from "@/components/gpu/usage/gpuUsagePeriod";

/**
 * GPU Quota 쿼터 탭의 조회 축. URL 이 소유한다(rules.md §17). 그래서 새로고침과 딥링크에서도
 * 유지된다.
 *
 * - `period`: 사용량 기간 프리셋이다. URL 에는 프리셋 키(`period=7d`)로 남고, 요청 시점에 절대
 *   시각으로 바꾼다. 절대 시각을 URL 에 고정하면 공유한 링크가 다음 날 "최근 1주"의 뜻을 잃는다.
 *   기간은 항상 있어야 한다. 그래서 다른 화면의 상대 기간 축과 달리 "전체"가 없고, 기본값이
 *   중립 자리를 대신한다.
 * - `query`: 대상 이름 검색어다. 받은 목록을 화면에서 거른다. 서버를 다시 호출하지 않는다.
 */
export const DEFAULT_GPU_QUOTA_USAGE_PERIOD: GpuUsagePeriod = "30d";

export type GpuQuotaFilterState = {
  period: GpuUsagePeriod;
  query: string;
};

export const EMPTY_GPU_QUOTA_FILTERS: GpuQuotaFilterState = {
  period: DEFAULT_GPU_QUOTA_USAGE_PERIOD,
  query: "",
};

const PARAM = { period: "period", query: "q" } as const;

// URL 값은 외부 입력이다. 정확한 어휘만 받는다.
function isGpuUsagePeriod(value: string | null): value is GpuUsagePeriod {
  return GPU_USAGE_PERIOD_OPTIONS.some((option) => option.value === value);
}

export function readGpuQuotaFilters(params: URLSearchParams): GpuQuotaFilterState {
  const period = params.get(PARAM.period);
  return {
    period: isGpuUsagePeriod(period) ? period : DEFAULT_GPU_QUOTA_USAGE_PERIOD,
    query: params.get(PARAM.query) ?? "",
  };
}

// 자기 키만 지우고 다시 쓴다. 입력은 변경하지 않는다. 기본값은 키를 지운다. 그래서 짧은 URL 이
// canonical 이 된다.
export function writeGpuQuotaFilters(
  params: URLSearchParams,
  state: GpuQuotaFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (state.period === DEFAULT_GPU_QUOTA_USAGE_PERIOD) {
    next.delete(PARAM.period);
  } else {
    next.set(PARAM.period, state.period);
  }
  if (state.query === "") {
    next.delete(PARAM.query);
  } else {
    next.set(PARAM.query, state.query);
  }
  return next;
}
